import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import { getUserProfile, getOrganization, setUserProfile } from '../firebase/firestore';
import { ensureOrgClaim } from '../firebase/claims';
import { Organization, UserProfile, OrgRole } from '../models/types';

interface OrgContextType {
  userProfile: UserProfile | null;
  organization: Organization | null;
  isLoading: boolean;
  isAdmin: boolean;
  /* The restricted external tier. Contractors reach only assigned projects and may only fill
     out facility assessments + view the report — mirrored from firestore.rules. */
  isContractor: boolean;
  role: OrgRole | null;
  orgId: string | null;
  refreshProfile: (opts?: { quiet?: boolean }) => Promise<void>;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within OrgProvider');
  return ctx;
};

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfileState] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const { awaitSetup } = useAuth();

  /* `quiet` skips the loading flag, so a membership change pushed by the listener below swaps the
     profile in place instead of flashing the whole app back to its loading state. */
  const loadProfile = useCallback(async (opts?: { quiet?: boolean }) => {
    if (!currentUser) {
      setUserProfileState(null);
      setOrganization(null);
      setIsLoading(false);
      return;
    }
    try {
      if (!opts?.quiet) setIsLoading(true);
      // Wait for any in-flight signup/login to finish applying invite before
      // reading Firestore — prevents the race where we create a bare profile
      // with no orgId and redirect the user to /setup unnecessarily.
      await awaitSetup();
      let profile = await getUserProfile(currentUser.uid);

      // Create a bare profile if one doesn't exist yet (e.g. Google sign-in first time)
      if (!profile) {
        await setUserProfile(currentUser.uid, {
          email: currentUser.email ?? '',
          displayName: currentUser.displayName ?? '',
        });
        profile = {
          uid: currentUser.uid,
          email: currentUser.email ?? '',
          displayName: currentUser.displayName ?? '',
          joinedAt: new Date().toISOString(),
        };
      }

      setUserProfileState(profile);

      if (profile.orgId) {
        // Storage rules authorize from the orgId claim, so make sure the token actually carries
        // it. Backgrounded: nothing here blocks rendering, and uploads retry on their own.
        void ensureOrgClaim(profile.orgId, profile.role ?? null);
        const org = await getOrganization(profile.orgId);
        setOrganization(org);
      } else {
        setOrganization(null);
      }
    } catch (err) {
      /* Keep the last-known-good profile and org on a transient read failure — do NOT null them.
         Nulling flipped `role` to null → isContractor false, which un-hid the calculation tools a
         contractor must never see (the "after visiting a failed report" regression: a flaky auth/
         network moment made loadProfile re-run and fail, wiping the role). A genuine membership
         change arrives as a *successful* read with a cleared orgId, not as a thrown error, so
         nothing legitimate needs the wipe. On the very first load there is simply nothing to keep. */
      console.error('[OrgContext] Failed to load profile (keeping last-known-good):', err);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, awaitSetup]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  /* Live membership. The profile used to be read once, so when an admin changed this user's role
     or removed them, nothing here noticed until a reload — the UI kept showing tools the rules
     now refused. Watch the user's own doc and re-run loadProfile (which also refreshes the token
     claims via ensureOrgClaim) when orgId or role actually changes. The first snapshot is
     loadProfile's job, so nothing happens until there's a profile to compare against. */
  const profileRef = useRef(userProfile);
  profileRef.current = userProfile;
  useEffect(() => {
    if (!currentUser) return;
    return onSnapshot(doc(db, 'users', currentUser.uid), snap => {
      const current = profileRef.current;
      const next = snap.data();
      if (!current || !next) return;
      if ((next.orgId ?? null) !== (current.orgId ?? null) || (next.role ?? null) !== (current.role ?? null)) {
        void loadProfile({ quiet: true });
      }
    }, err => console.warn('[OrgContext] profile listener failed', err));
  }, [currentUser, loadProfile]);

  const isAdmin = !!(userProfile && organization && userProfile.role === 'admin' && userProfile.orgId === organization.id);
  const role = userProfile?.role ?? null;
  const isContractor = role === 'contractor';
  const orgId = userProfile?.orgId ?? null;

  return (
    <OrgContext.Provider value={{ userProfile, organization, isLoading, isAdmin, isContractor, role, orgId, refreshProfile: loadProfile }}>
      {children}
    </OrgContext.Provider>
  );
};
